import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import AICreditsDiscoveryBanner from 'components/pages/AICreditsDiscoveryBanner'
import GlobalBanner from 'components/pages/GlobalBanner'
import PhishingSOCAgentBanner from 'components/pages/PhishingSOCAgentBanner'

import { selectShowsPovState } from '../Agents/creditSelectors'
import { selectGetPriceBanner, selectShowMeBanner } from '../Agents/selectors'
import { selectData } from '../App/selectors'

import { dismissDiscoveryBanner } from './actions'
import { FETCH_DISCOVERY_BANNER_ASYNC } from './constants'
import { selectDiscoveryBanner, selectMailboxesSyncing, selectShowDemoBanner } from './selectors'

/**
 * CombinedBanner - Renders the appropriate banner based on conditions
 *
 * Priority: AICreditsDiscoveryBanner > PhishingSOCAgentBanner > GlobalBanner (other types)
 * Returns only the banner content - no children wrapping needed
 * AppLayout handles the positioning
 */
export const CombinedBanner = ({
  data,
  showDemoBanner,
  mailboxesSyncing,
  history,
  location,
  phishingSOCBanner,
  discoveryBanner,
  dismissDiscoveryBanner: onDismissDiscoveryBanner,
  fetchDiscoveryBanner,
  showsPovState,
}) => {
  // Ref (not state) so a showBanner flip alone never re-triggers this effect —
  // only a route change does. isFirstRender skips the mount run, since the
  // app-load fetch (fetchDiscoveryBanner epic) already covers it.
  const showBannerRef = useRef(discoveryBanner.showBanner)
  showBannerRef.current = discoveryBanner.showBanner
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    // IC-49444: credits can be exhausted mid-session; re-fetch on navigation so
    // the banner hides without a full page refresh. The banner never re-shows
    // mid-session (only on next login), so skip once it's already hidden.
    if (showBannerRef.current) {
      fetchDiscoveryBanner()
    }
  }, [location.pathname, fetchDiscoveryBanner])

  // PLG discovery banner has top priority while the tenant is on preview credits
  // (PLG Experience §1.1 — backend gates visibility via showDiscoveryBanner/reason).
  // POV / billing-bypassed tenants render the POV surfaces, never PLG-plan UI,
  // even if the backend still reports showBanner for them.
  if (discoveryBanner.showBanner && !showsPovState) {
    return <AICreditsDiscoveryBanner onDismiss={onDismissDiscoveryBanner} />
  }

  // PhishingSOCAgentBanner has priority if it should show
  if (phishingSOCBanner.show) {
    return <PhishingSOCAgentBanner type={phishingSOCBanner.type} history={history} />
  }

  // Otherwise render GlobalBanner (handles silent mode, demo mode, etc.)
  return (
    <GlobalBanner
      data={data}
      showDemoBanner={showDemoBanner}
      mailboxesSyncing={mailboxesSyncing}
      history={history}
    />
  )
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { dismissDiscoveryBanner, fetchDiscoveryBanner: FETCH_DISCOVERY_BANNER_ASYNC.initiate },
    dispatch
  )
const mapStateToProps = (state) => ({
  data: selectData(state),
  showDemoBanner: selectShowDemoBanner(state),
  mailboxesSyncing: selectMailboxesSyncing(state),
  discoveryBanner: selectDiscoveryBanner(state),
  showsPovState: selectShowsPovState(state),
  phishingSOCBanner: {
    show: selectShowMeBanner(state) || selectGetPriceBanner(state),
    type: selectShowMeBanner(state) ? 'initial' : 'upgrade',
  },
})

const render = connect(mapStateToProps, mapDispatchToProps)(CombinedBanner)

export default withRouter(render)
